import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import PremiumLock from '../components/PremiumLock';
import TabNotation from '../components/TabNotation';
import useTheoryAudio from '../hooks/useTheoryAudio';
import { musicXmlToEvents } from '../utils/musicxmlAudio';
import {
  GraduationCap, Crown, Play, Loader2, Volume2, X, Check, Target, Lightbulb,
  Clock, BookOpen,
} from 'lucide-react';

const VerovioNotation = lazy(() => import('../components/VerovioNotation'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STREAM_LABELS = {
  classical: { en: 'Classical', it: 'Classica', accent: 'amber' },
  fingerstyle: { en: 'Fingerstyle', it: 'Fingerstyle', accent: 'purple' },
  blues: { en: 'Blues', it: 'Blues', accent: 'blue' },
  jazz: { en: 'Jazz', it: 'Jazz', accent: 'green' },
  rock: { en: 'Rock', it: 'Rock', accent: 'red' },
};

// Studies are grouped into collections so the displayed number matches
// the source method (Sagreras Lección number); see backend _classify.
const COLLECTION_LABELS = {
  preliminary: { en: 'Preliminary studies', it: 'Studi preliminari' },
  'sagreras-1': { en: 'Sagreras — Book 1', it: 'Sagreras — Libro 1' },
  'sagreras-1-scales': { en: 'Sagreras — Book 1, scales', it: 'Sagreras — Libro 1, scale' },
  'sagreras-2': { en: 'Sagreras — Book 2', it: 'Sagreras — Libro 2' },
};
const COLLECTION_ORDER = ['preliminary', 'sagreras-1', 'sagreras-1-scales', 'sagreras-2'];

// Split a stream's etudes into ordered [collection, items] groups, each
// sorted by the displayed collection number (so a study added with a
// high internal level still lands in its pedagogical position).
function groupByCollection(list) {
  const groups = {};
  for (const e of list) {
    const key = e.collection || '_';
    (groups[key] = groups[key] || []).push(e);
  }
  const ordered = COLLECTION_ORDER.filter((k) => groups[k]);
  const rest = Object.keys(groups).filter((k) => !ordered.includes(k));
  return [...ordered, ...rest].map((k) => [
    k,
    groups[k].sort((a, b) => (a.number ?? a.level) - (b.number ?? b.level)),
  ]);
}

// Audio timeline from the etude's MusicXML (musicxmlAudio.js).
const buildAudioSpec = (etude, bpm) => {
  if (!etude?.musicxml) return null;
  const quarterSec = 60 / bpm;
  const events = musicXmlToEvents(etude.musicxml);
  if (events.length === 0) return null;
  return {
    type: 'polyphony',
    events: events.map((e) => ({
      note: e.note,
      start: e.startQuarters * quarterSec,
      duration: e.durationQuarters * quarterSec,
    })),
    instrument: etude.audio_instrument || 'acoustic_guitar_nylon',
  };
};

const EtudeDetail = ({ etude, onClose, lang }) => {
  const { t } = useTranslation();
  const [bpm, setBpm] = useState(etude.tempo_curve?.[0]?.bpm || 60);
  const { play, playing, ready } = useTheoryAudio({ instrument: 'acoustic_guitar_nylon' });

  const handlePlay = () => {
    const spec = buildAudioSpec(etude, bpm);
    console.info('[EtudesPage] Play clicked.', {
      etudeId: etude.id,
      bpm,
      ready,
      playing,
      specBuilt: !!spec,
      noteCount: spec?.notes?.length,
      firstNotes: spec?.notes?.slice(0, 5),
      instrument: spec?.instrument,
    });
    if (!spec) {
      console.warn('[EtudesPage] No audio spec built (no parseable notes in musicxml)');
      return;
    }
    if (!ready) {
      console.warn('[EtudesPage] Audio not ready (soundfont still loading or failed to load)');
      return;
    }
    if (playing) {
      console.warn('[EtudesPage] Already playing');
      return;
    }
    play(spec);
  };

  const title = lang === 'it' ? etude.title_it : etude.title;
  const objective = lang === 'it' ? etude.objective_it : etude.objective;
  const checklist = lang === 'it' ? etude.checklist_it : etude.checklist;
  const tips = lang === 'it' ? etude.tips_it : etude.tips;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen p-4 flex items-start justify-center" onClick={(e) => e.stopPropagation()}>
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="w-full max-w-4xl bg-[#0F0F0F] border border-white/10 rounded-3xl my-8 overflow-hidden"
        >
          <div className="flex items-start justify-between p-6 border-b border-white/5">
            <div>
              <div className="flex items-center gap-2 text-xs text-purple-300 mb-2 uppercase tracking-wider">
                <span className="text-zinc-500">Stream:</span>
                <span>{STREAM_LABELS[etude.stream]?.[lang] || etude.stream}</span>
                {etude.collection && COLLECTION_LABELS[etude.collection] && (
                  <>
                    <span className="text-zinc-500">/</span>
                    <span>{COLLECTION_LABELS[etude.collection][lang]}</span>
                  </>
                )}
              </div>
              <h2 className="font-heading text-3xl font-black text-white">{title}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-zinc-400 hover:text-white p-2"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Objective */}
            <section>
              <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                <Target className="w-4 h-4" /> {lang === 'it' ? 'Obiettivo' : 'Objective'}
              </h3>
              <p className="text-zinc-200 leading-relaxed">{objective}</p>
            </section>

            {/* Original source image (if available). Shown above the
                rendered score because the original is always faithful
                while the transcription may be approximate. */}
            {etude.source_image && (
              <section>
                <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>{lang === 'it' ? 'Originale' : 'Original'}</span>
                  {etude.composer && (
                    <span className="text-zinc-500 text-xs normal-case tracking-normal">{etude.composer}</span>
                  )}
                </h3>
                <div className="bg-white rounded-xl p-3 sm:p-4 border border-zinc-300 overflow-x-auto">
                  <img
                    src={etude.source_image}
                    alt={`${etude.title_it || etude.title} - ${lang === 'it' ? 'spartito originale' : 'original score'}`}
                    className="w-full h-auto block max-w-none"
                    style={{ minWidth: '500px' }}
                  />
                </div>
                {etude.original_reference && (
                  <p className="text-zinc-500 text-xs mt-2 italic">{etude.original_reference}</p>
                )}
              </section>
            )}

            {/* Standard notation, rendered by Verovio. */}
            {etude.musicxml && (
              <section>
                <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-2">
                  {lang === 'it' ? 'Partitura' : 'Score'}
                </h3>
                <Suspense fallback={<div className="text-zinc-500">...</div>}>
                  <VerovioNotation>{etude.musicxml}</VerovioNotation>
                </Suspense>
                {/* Imported scores (Mutopia, OMR) carry their licence; CC
                    licences require the attribution to be visible. */}
                {etude.license && (
                  <p className="text-zinc-500 text-xs mt-2">
                    {lang === 'it' ? 'Fonte' : 'Source'}:{' '}
                    {etude.source_url ? (
                      <a href={etude.source_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-zinc-300">
                        {new URL(etude.source_url).hostname}
                      </a>
                    ) : (etude.composer || '-')}
                    {' · '}
                    {lang === 'it' ? 'Licenza' : 'License'}: {etude.license}
                  </p>
                )}
              </section>
            )}

            {/* Tab */}
            {etude.tab_block && (
              <section>
                <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-2">Tab</h3>
                <TabNotation>{etude.tab_block}</TabNotation>
              </section>
            )}

            {/* Tempo + audio */}
            <section>
              <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" /> {lang === 'it' ? 'Tempo' : 'Tempo'}
              </h3>
              <div className="flex items-center gap-3 flex-wrap">
                {etude.tempo_curve?.map((stage) => (
                  <button
                    key={stage.bpm}
                    type="button"
                    onClick={() => setBpm(stage.bpm)}
                    className={`px-4 py-2 rounded-lg border text-sm transition ${
                      bpm === stage.bpm
                        ? 'border-purple-400 bg-purple-500/20 text-white'
                        : 'border-white/10 text-zinc-300 hover:border-white/30'
                    }`}
                  >
                    <span className="font-mono font-bold">{stage.bpm}</span>
                    <span className="text-zinc-500 ml-2">
                      {lang === 'it' ? stage.label_it : stage.label}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={handlePlay}
                  disabled={!ready || playing}
                  className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/30 hover:bg-purple-500/40 border border-purple-400/50 text-purple-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {playing ? <Loader2 className="w-4 h-4 animate-spin" />
                    : !ready ? <Volume2 className="w-4 h-4" />
                    : <Play className="w-4 h-4" />}
                  {lang === 'it' ? `Suona a ${bpm} BPM` : `Play at ${bpm} BPM`}
                </button>
              </div>
            </section>

            {/* Theory lessons */}
            {etude.theory_lessons && etude.theory_lessons.length > 0 && (
              <section>
                <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" /> {lang === 'it' ? 'Teoria collegata' : 'Linked theory'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {etude.theory_lessons.map((lid) => (
                    <Link
                      key={lid}
                      to={`/theory?focus=${lid}`}
                      className="text-purple-300 hover:text-purple-100 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-lg px-3 py-1.5 text-sm font-mono"
                    >
                      {lid}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Checklist */}
            {checklist && checklist.length > 0 && (
              <section>
                <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4" /> {lang === 'it' ? 'Quando è "fatto"' : 'Mastery checklist'}
                </h3>
                <ul className="space-y-2">
                  {checklist.map((c, i) => (
                    <li key={i} className="flex items-start gap-3 text-zinc-200">
                      <span className="mt-1 w-4 h-4 rounded border border-purple-400/50 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Tips */}
            {tips && tips.length > 0 && (
              <section>
                <h3 className="text-purple-300 text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> Tips
                </h3>
                <ul className="space-y-2">
                  {tips.map((tip, i) => (
                    <li key={i} className="text-zinc-300 leading-relaxed">- {tip}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Empty-state with a "populate now" button that triggers the backend
// seed endpoint. Saves the user from having to send a manual HTTP POST
// from an iPad / browser without a terminal.
const EmptyState = ({ lang, onSeeded }) => {
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSeed = async () => {
    setSeeding(true);
    setMessage(null);
    try {
      await axios.post(`${API}/seed/etudes`);
      const res = await axios.get(`${API}/etudes`);
      onSeeded(res.data || []);
    } catch (e) {
      setMessage(lang === 'it'
        ? 'Errore durante la popolazione. Il backend potrebbe essere ancora in riavvio.'
        : 'Seeding failed. The backend may still be starting up.');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6 text-zinc-300">
      <p className="font-medium text-amber-200 mb-3">
        {lang === 'it' ? 'Nessuno studio in archivio.' : 'No etudes yet.'}
      </p>
      <p className="text-sm mb-4">
        {lang === 'it'
          ? 'La collezione e\' vuota. Tocca il bottone per popolarla con gli studi disponibili.'
          : 'The collection is empty. Tap the button to populate it with the available etudes.'}
      </p>
      <button
        type="button"
        onClick={handleSeed}
        disabled={seeding}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/30 hover:bg-amber-500/40 border border-amber-400/50 text-amber-100 disabled:opacity-40"
      >
        {seeding && <Loader2 className="w-4 h-4 animate-spin" />}
        {seeding
          ? (lang === 'it' ? 'Popolazione in corso...' : 'Seeding...')
          : (lang === 'it' ? 'Popola la collezione' : 'Populate now')}
      </button>
      {message && <p className="text-amber-300 text-sm mt-3">{message}</p>}
    </div>
  );
};

const EtudesPage = () => {
  const { t, i18n } = useTranslation();
  const { isPremium } = useAuth();
  const [etudes, setEtudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const lang = i18n.language === 'it' ? 'it' : 'en';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/etudes`);
        const list = res.data || [];
        console.info(`[EtudesPage] Loaded ${list.length} etudes from ${API}/etudes`);
        if (list.length > 0) {
          const ids = list.map((e) => e.id).join(', ');
          console.info(`[EtudesPage] IDs: ${ids}`);
        }
        if (!cancelled) setEtudes(list);
      } catch (e) {
        console.error('Error fetching etudes:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Group by stream, sorted by level
  const byStream = etudes.reduce((acc, e) => {
    (acc[e.stream] = acc[e.stream] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#070707] text-zinc-100">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <GraduationCap className="w-8 h-8 text-purple-400" />
            <h1 className="font-heading text-4xl font-black tracking-tight">
              {lang === 'it' ? 'Studi' : 'Etudes'}
            </h1>
          </div>
          <p className="text-zinc-400 max-w-2xl leading-relaxed">
            {lang === 'it'
              ? 'Brani brevi che insegnano una cosa tecnica per volta. La difficoltà cresce progressivamente come nelle Lecciones di Sagreras. Ogni studio è collegato alla teoria che lo motiva.'
              : 'Short pieces that teach one technical thing at a time. Difficulty grows progressively in the spirit of Sagreras\' Lecciones. Each etude is linked to the theory that motivates it.'}
          </p>
        </header>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin" /> {lang === 'it' ? 'Caricamento...' : 'Loading...'}
          </div>
        ) : etudes.length === 0 ? (
          <EmptyState lang={lang} onSeeded={(list) => setEtudes(list)} />
        ) : (
          Object.entries(byStream).map(([stream, list]) => (
            <section key={stream} className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-white mb-4">
                {STREAM_LABELS[stream]?.[lang] || stream}
                <span className="text-zinc-500 text-sm font-mono ml-3">{list.length}</span>
              </h2>
              {groupByCollection(list).map(([collection, items]) => (
                <div key={collection} className="mb-8">
                  {COLLECTION_LABELS[collection] && (
                    <h3 className="text-sm font-semibold text-purple-300/90 uppercase tracking-wider mb-3">
                      {COLLECTION_LABELS[collection][lang]}
                      <span className="text-zinc-600 font-mono ml-2">{items.length}</span>
                    </h3>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((etude) => (
                  <button
                    key={etude.id}
                    type="button"
                    onClick={() => etude.locked ? null : setSelected(etude)}
                    className={`text-left bg-[#0F0F0F] border border-white/5 hover:border-purple-500/40 rounded-2xl p-5 transition relative ${etude.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {etude.locked && (
                      <div className="absolute top-3 right-3 text-amber-300">
                        <Crown className="w-4 h-4" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-purple-300 mb-2 uppercase tracking-wider font-mono">
                      <span>{etude.duration_minutes_estimate} min</span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {lang === 'it' ? etude.title_it : etude.title}
                    </h3>
                    <p className="text-zinc-400 text-sm line-clamp-2">
                      {lang === 'it' ? etude.objective_it : etude.objective}
                    </p>
                  </button>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))
        )}
      </div>

      {selected && (
        <EtudeDetail etude={selected} onClose={() => setSelected(null)} lang={lang} />
      )}
    </div>
  );
};

export default EtudesPage;
